package dev.lingvo.site.components.studio.compose

import dev.lingvo.site.components.studio.fonts.FontLoadProgress
import dev.lingvo.shared.RenderCellRef
import dev.lingvo.shared.ScreenshotSpecs
import dev.lingvo.shared.StorePlatform
import dev.lingvo.shared.screenshots.render.RenderReport
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.XhrUpload
import dev.lingvo.site.api.putScreenshotRender
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.delay
import kotlinx.coroutines.ensureActive
import org.w3c.files.Blob
import kotlin.js.Date
import dev.lingvo.site.i18n.t

/** Bounded live browser resources; counters are zero after every terminal path. */
data class ComposeAllocations(var canvases: Int = 0, var bitmaps: Int = 0, var blobs: Int = 0)

/** A failed cell remains actionable instead of disappearing from the run. */
data class ComposeFailure(val job: ComposeJob, val reason: String)

/** Immutable UI progress snapshot. */
data class ComposeProgress(
    val total: Int = 0, val done: Int = 0, val failures: List<ComposeFailure> = emptyList(),
    val current: ComposeJob? = null, val running: Boolean = false, val cancelled: Boolean = false,
    val fontProgress: FontLoadProgress? = null, val message: String? = null, val remainingSeconds: Int? = null, val completedCells: Set<RenderCellRef> = emptySet(),
)

/** Sequential export keeps only one canvas, one decoded capture and one upload body live.
 * Upload/render overlap is deliberately omitted to lower the peak allocation and cancellation complexity.
 */
class ComposeRunner(
    private val renderer: ComposeRenderPort,
    private val capture: suspend (ComposeJob) -> dynamic,
    private val upload: suspend (ComposeJob, Blob, RenderReport, String?, String) -> Unit,
    private val authority: () -> Boolean,
    private val pause: suspend (Long) -> Unit = { delay(it.coerceAtLeast(1)) },
) {
    private val primaryAllocations = ComposeAllocations()
    val allocations: ComposeAllocations get() = primaryAllocations.copy(bitmaps = primaryAllocations.bitmaps + renderer.ownedBitmaps)

    /** Pass to fetchComposeImage to include the transient encoded capture body in resource telemetry. */
    fun captureBlobCountChanged(delta: Int) { primaryAllocations.blobs += delta }

    /** Executes export jobs; cancellation propagates after deterministic cleanup. */
    suspend fun run(jobs: List<ComposeJob>, progress: (ComposeProgress) -> Unit) {
        var state = ComposeProgress(total = jobs.size, running = true)
        progress(state)
        val startedAt = Date.now()
        var canvas: dynamic = null
        try {
            check(authority()) { tr("studio.compose.compose_runner.compose_permission_or_session_changed", "Compose permission or session changed") }
            check(BrowserDeflate.available || jobs.all { it.width.toLong() * it.height * 3 + it.height + 1024 < ScreenshotSpecs.MAX_IMAGE_BYTES }) {
                tr("studio.compose.compose_runner.this_browser_cannot_compress_these_screenshots", "This browser cannot compress these screenshots below the store limit. Update your browser and retry.")
            }
            val fonts = renderer.ensureFonts(jobs) { download ->
                state = state.copy(fontProgress = download)
                progress(state)
            }
            check(fonts.ready) { fonts.message ?: tr("studio.compose.compose_runner.the_screenshot_fonts_did_not_load", "The screenshot fonts did not load. Retry.") }
            state = state.copy(fontProgress = null)
            canvas = if (js("typeof OffscreenCanvas !== 'undefined'") as Boolean) js("new OffscreenCanvas(1, 1)")
                else document.createElement("canvas").asDynamic()
            primaryAllocations.canvases++
            for (job in jobs) {
                currentCoroutineContext().ensureActive()
                check(authority()) { tr("studio.compose.compose_runner.compose_permission_or_session_changed", "Compose permission or session changed") }
                state = state.copy(current = job)
                progress(state)
                var bitmap: dynamic = null
                try {
                    check(job.refused == null) { tr("studio.compose.compose_runner.caption_does_not_fit_at_the", "Caption does not fit at the minimum size") }
                    renderer.prepare(job)
                    if (job.captureId != null) {
                        bitmap = capture(job)
                        primaryAllocations.bitmaps++
                    }
                    currentCoroutineContext().ensureActive()
                    canvas.width = job.width
                    canvas.height = job.height
                    val report = renderer.render(job, bitmap, canvas)
                    if (bitmap != null) {
                        bitmap.close()
                        bitmap = null
                        primaryAllocations.bitmaps--
                    }
                    renderer.closeJob()
                    check(!report.blocking) { tr("studio.compose.compose_runner.render_has_missing_inputs_or_text", "Render has missing inputs or text below its fit floor") }
                    val text = verifiedComposeCaption(job, report)
                    val captionHash = text?.let { composeSha256(it.encodeToByteArray()) }
                    check(text == null || (text == job.caption.text && captionHash == job.caption.sha256)) { tr("studio.compose.compose_runner.caption_changed_since_planning", "Caption changed since planning") }
                    val png = encodeComposeCanvas(canvas, job.width, job.height)
                    check(png.size <= ScreenshotSpecs.MAX_IMAGE_BYTES) { tr("studio.compose.compose_runner.this_screenshot_exceeds_the_store_image", "This screenshot exceeds the store image limit. Reduce artwork detail or update your browser, then retry.") }
                    val pngHash = composeSha256(png)
                    currentCoroutineContext().ensureActive()
                    check(authority()) { tr("studio.compose.compose_runner.compose_permission_or_session_changed", "Compose permission or session changed") }
                    val body = composeBlob(png)
                    primaryAllocations.blobs++
                    try { upload(job, body, report, captionHash, pngHash) }
                    finally { primaryAllocations.blobs-- }
                    state = state.copy(done = state.done + 1, completedCells = state.completedCells + RenderCellRef(job.slotKey, job.device, job.locale))
                } catch (cancelled: CancellationException) { throw cancelled }
                catch (failure: Throwable) {
                    state = state.copy(failures = state.failures + ComposeFailure(job, failure.message ?: tr("studio.compose.compose_runner.compose_failed", "Compose failed")))
                } finally {
                    if (bitmap != null) { bitmap.close(); primaryAllocations.bitmaps-- }
                    renderer.closeJob()
                }
                val processed = state.done + state.failures.size
                if (processed >= 5) state = state.copy(remainingSeconds =
                    (((Date.now() - startedAt) / processed) * (jobs.size - processed) / 1000).toInt())
                progress(state)
                val memory = window.asDynamic().performance.memory
                val used = if (memory != null) (memory.usedJSHeapSize as? Number)?.toLong() else null
                val limit = if (memory != null) (memory.jsHeapSizeLimit as? Number)?.toLong() else null
                pause(BitmapBudget(job.width, job.height).pauseMillis(used, limit).toLong())
            }
        } catch (cancelled: CancellationException) {
            state = state.copy(cancelled = true)
            throw cancelled
        } catch (failure: Throwable) { state = state.copy(message = failure.message ?: tr("studio.compose.compose_runner.compose_stopped", "Compose stopped")) }
        finally {
            renderer.closeJob()
            if (canvas != null) { canvas.width = 0; canvas.height = 0; primaryAllocations.canvases-- }
            progress(state.copy(running = false, current = null, fontProgress = null))
        }
    }
}

/** R21 uploader retains exact capture/caption provenance; XhrUpload aborts with the coroutine. */
fun screenshotComposeUploader(slug: String, platform: StorePlatform, floorPx: (ComposeJob) -> Double): suspend (ComposeJob, Blob, RenderReport, String?, String) -> Unit =
    { job, body, report, captionHash, _ ->
        ApiClient.putScreenshotRender(slug, platform, job.slotId, job.device, job.locale, job.designVersion,
            renderUploadFit(report, floorPx(job), job.schemaVersion), body, "image/png",
            job.captureId, job.captureSha256, captionHash, XhrUpload())
        Unit
    }

/** Keeps the ImageData and its zero-copy signed view out of the upload suspension frame. */
private suspend fun encodeComposeCanvas(canvas: dynamic, width: Int, height: Int): ByteArray {
    val pixels: dynamic = canvas.getContext("2d").getImageData(0, 0, width, height).data
    val rgba = js("new Int8Array(pixels.buffer, pixels.byteOffset, pixels.byteLength)").unsafeCast<ByteArray>()
    return RgbPngEncoder.encode(width, height, rgba, BrowserDeflate)
}
